import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, MeetDashboardData, Meeting, MeetingPick, MeetingStatus, MonitorRange, SlotsData } from './crmApi';
import { Empty, Head, Modal, Spinner, Tabs, fmtPhone, fmtWhen, inputCls, labelCls, purpleBg } from './ui';
import { C, Card, Columns, Tile, nf } from './viz';

// ── Student Coordinator meetings: the sales side ─────────────
// Log call → "Coordinator Meeting" books a 1-hour time when a coordinator is
// free (SlotPicker: times only, no names). Every coordinator free then hears
// it; the first to add the meeting link (Google Meet, Zoom, Teams) takes it.
// The salesperson then confirms it and sends it to the student on WhatsApp
// with the ready message (MeetingCard). After the meeting the coordinator
// marks whether the student joined. Managers see every meeting and each
// coordinator's numbers (MeetNumbers, also the coordinators' own dashboard).
// Mirrors the app's crm_meetings_screen.dart.

export const STATUS_STYLE: Record<MeetingStatus, { bg: string; fg: string; label: string }> = {
  'Booked':     { bg: '#fffbeb', fg: '#a16207', label: 'Waiting for the link' },
  'Link Ready': { bg: '#fef2f2', fg: '#b91c1c', label: 'Link ready: send it' },
  'Confirmed':  { bg: '#eef2ff', fg: '#4338ca', label: 'Sent to the student' },
  'Joined':     { bg: '#f0fdf4', fg: '#15803d', label: 'Joined' },
  'Not Joined': { bg: '#f3f4f6', fg: '#4b5563', label: "Didn't join" },
  'Cancelled':  { bg: '#f3f4f6', fg: '#6b7280', label: 'Cancelled' },
};

export const MeetingChip: React.FC<{ m: Meeting; coordinator?: boolean }> = ({ m, coordinator }) => {
  const s = STATUS_STYLE[m.status];
  // Over and not marked yet: the coordinator still has to say what happened.
  // Open: nobody took it yet (a coordinator takes it by adding the link).
  const label = m.active && m.ended ? 'Over: not marked yet' : m.active && m.started ? 'On now'
    : m.open && m.status === 'Booked' ? (coordinator ? 'Open: add your link to take it' : 'No coordinator yet')
    : coordinator && m.status === 'Booked' ? 'Add the link' : coordinator && m.status === 'Link Ready' ? 'Link added' : s.label;
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: s.bg, color: s.fg }}>{label}</span>;
};

// wa.me with the ready message (the number with 91)
export const whatsappUrl = (phone: string, text = '') => `https://wa.me/${phone}${text ? `?text=${encodeURIComponent(text)}` : ''}`;

// ── Picking a time ───────────────────────────────────────────

export const SlotPicker: React.FC<{ auth: CrmAuth; value: MeetingPick | null; onPick: (p: MeetingPick | null) => void; meetingId?: string }> = ({ auth, value, onPick, meetingId }) => {
  const [data, setData] = useState<SlotsData | null>(null);
  const [error, setError] = useState('');
  const [day, setDay] = useState('');
  useEffect(() => {
    let live = true;
    crm.meetSlots(auth, meetingId).then(r => {
      if (!live) return;
      setData(r);
      setDay((r.days.find(d => d.slots.length) || r.days[0])?.date || '');
    }).catch(e => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [auth, meetingId]);

  if (error) return <p className="text-sm text-red-700 bg-red-50 rounded-xl p-3">{error}</p>;
  if (!data) return <Spinner label="Loading the free times…" />;
  const total = data.days.reduce((n, d) => n + d.slots.length, 0);
  if (!total) {
    return (
      <p className="text-sm text-amber-800 bg-amber-50 rounded-xl p-3">
        {data.coordinators.some(c => c.hasTimes) ? 'No free meeting times in the next working days. Ask a Student Coordinator to add more times.' : 'No Student Coordinator has set their free times yet.'}
      </p>
    );
  }
  const current = data.days.find(d => d.date === day) || data.days[0];
  // Times only: whoever is free then takes the meeting (no names)
  const slots = current.slots.filter((s, i, all) => all.findIndex(x => x.slot === s.slot) === i);
  const picked = (s: { date: string; slot: string }) => !!value && value.date === s.date && value.slot === s.slot;
  return (
    <div className="space-y-3">
      <div className="flex gap-1.5 overflow-x-auto pb-1" role="tablist" aria-label="Day">
        {data.days.map(d => {
          const n = new Set(d.slots.map(s => s.slot)).size;
          return (
            <button key={d.date} type="button" role="tab" aria-selected={d.date === current.date} onClick={() => setDay(d.date)} disabled={!n}
              className={`px-3 py-2 rounded-xl text-xs font-bold border-2 whitespace-nowrap disabled:opacity-40 ${d.date === current.date ? 'border-indigo-600 bg-indigo-50 text-indigo-900' : 'border-gray-200 bg-white text-gray-600'}`}>
              {d.label}<span className="block text-[10px] font-semibold opacity-70">{n ? `${n} free` : 'none free'}</span>
            </button>
          );
        })}
      </div>
      {slots.length === 0 ? <p className="text-xs text-gray-500">No free times on {current.label}.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" role="radiogroup" aria-label={`Times on ${current.label}`}>
          {slots.map(s => (
            <button key={`${s.date}-${s.slot}`} type="button" role="radio" aria-checked={picked(s)}
              onClick={() => onPick(picked(s) ? null : { coordinatorId: s.coordinatorId, date: s.date, slot: s.slot })}
              className="px-3 py-2.5 rounded-xl border-2 text-left transition-all"
              style={picked(s) ? { background: '#312e81', borderColor: '#312e81', color: '#fff' } : { background: '#fff', borderColor: '#e5e7eb', color: '#1f2937' }}>
              <span className="block text-sm font-bold">{s.label}</span>
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-gray-400">Each meeting is {data.minutes / 60 === 1 ? '1 hour' : `${data.minutes} minutes`}. A time can be booked until {data.closeBefore} minutes before it starts. Every coordinator free then hears about it; the first to add the meeting link takes it, and you then send it to the student.</p>
    </div>
  );
};

// ── One meeting, for the salesperson (and managers) ──────────

// A pasted invite's Meeting ID and passcode (Teams, Zoom): they go in the WhatsApp message too
export const JoinCode: React.FC<{ m: Meeting }> = ({ m }) => (m.joinId || m.passcode ? (
  <p className="text-[11px] text-gray-600 mt-1">{[m.joinId && `Meeting ID: ${m.joinId}`, m.passcode && `Passcode: ${m.passcode}`].filter(Boolean).join(' · ')}</p>
) : null);

// "with Jamuna", or nobody yet (an open meeting: the first link takes it)
export const withWhom = (m: Meeting) => (m.open ? 'no coordinator yet' : `with ${m.coordinatorName}`);

const Detail: React.FC<{ m: Meeting }> = ({ m }) => {
  if (m.status === 'Booked' && m.open && !m.ended) return <>Waiting for a coordinator to take it: every coordinator free then can, the first to add the link gets it</>;
  if (m.status === 'Booked' && !m.ended) return <>Waiting for {m.coordinatorName} to add the meeting link</>;
  if (m.status === 'Link Ready' && !m.ended) return <>Link ready: confirm and send it to the student</>;
  if (m.status === 'Confirmed' && !m.ended) return <>Sent to the student{m.confirmedAt ? ` ${fmtWhen(m.confirmedAt)}` : ''}{m.confirmedByName ? ` by ${m.confirmedByName}` : ''}</>;
  if (m.active) return <>Over: waiting for {m.coordinatorName} to mark whether they joined</>;
  if (m.status === 'Joined') return <>Joined{m.withCoordinator ? `: ${m.withCoordinator} handles the lead now` : ''}</>;
  if (m.status === 'Not Joined') return <>Didn't join: on the No Response list. Try them again any time.</>;
  return <>Cancelled by {m.cancelledByName}{m.cancelReason ? `: ${m.cancelReason}` : ''}</>;
};

export const MeetingCard: React.FC<{ auth: CrmAuth; m: Meeting; onChanged: () => void; onOpenLead?: (leadId: string) => void; showOwner?: boolean }> = ({ auth, m, onChanged, onOpenLead, showOwner }) => {
  const [moving, setMoving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [busy, setBusy] = useState(false);
  const canSend = (m.status === 'Link Ready' || m.status === 'Confirmed') && !m.ended && !!m.invite && !!m.waPhone;
  // WhatsApp opens in the click itself (a pop-up blocker allows that), then it is marked sent
  const send = async () => {
    if (!m.waPhone || !m.invite) return;
    window.open(whatsappUrl(m.waPhone, m.invite), '_blank', 'noopener');
    if (m.status === 'Confirmed') return;
    setBusy(true);
    try {
      await crm.meetConfirm(auth, m.id);
      toast.success(`Sent to ${m.name}: the meeting is confirmed`);
      onChanged();
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard?.writeText(m.invite || m.link).then(() => toast.success('Copied'), () => {});
  return (
    <div className={`bg-white rounded-2xl border-2 p-4 space-y-3 ${m.status === 'Link Ready' && !m.ended ? 'border-red-200' : 'border-gray-100'}`}>
      <div className="flex items-start justify-between gap-3">
        <button type="button" onClick={() => onOpenLead?.(m.leadId)} className="text-left min-w-0" disabled={!onOpenLead}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-gray-800">{m.name || 'No name'}</span>
            <MeetingChip m={m} />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{m.phone ? fmtPhone(m.phone) : ''}{m.course ? ` · ${m.course}` : ''}{m.mode ? ` · ${m.mode}` : ''}</p>
          {showOwner && <p className="text-[11px] text-gray-400 mt-0.5">#{m.leadNo} · {m.salesPersonName || m.ownerName || 'No salesperson'}</p>}
        </button>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-bold text-indigo-900">{m.dayLabel}</p>
          <p className="text-xs font-semibold text-gray-600">{m.timeLabel}</p>
          <p className="text-[11px] text-gray-400">{withWhom(m)}</p>
        </div>
      </div>
      <p className="text-xs text-gray-600"><Detail m={m} /></p>
      {m.link && m.active && (
        <div className="rounded-xl bg-gray-50 px-3 py-2">
          <div className="flex items-center gap-2">
            <a href={m.link} target="_blank" rel="noreferrer" className="text-xs font-semibold text-indigo-700 truncate flex-1 min-w-0">{m.app ? `${m.app}: ` : ''}{m.link}</a>
            <button type="button" onClick={copy} className="text-[11px] font-bold text-gray-500 hover:text-gray-800">Copy</button>
          </div>
          <JoinCode m={m} />
        </div>
      )}
      {m.active && !m.started && (
        <div className="flex flex-wrap gap-2">
          {canSend && (
            <button type="button" onClick={send} disabled={busy} className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-60" style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)' }}>
              {m.status === 'Confirmed' ? '📲 Send on WhatsApp again' : '📲 Confirm & send on WhatsApp'}
            </button>
          )}
          <button type="button" onClick={() => setMoving(true)} className="px-3 py-2 rounded-xl text-xs font-bold border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Change time</button>
          <button type="button" onClick={() => setCancelling(true)} className="px-3 py-2 rounded-xl text-xs font-bold border-2 border-red-100 text-red-700 hover:bg-red-50">Cancel meeting</button>
        </div>
      )}
      {moving && <MoveMeetingModal auth={auth} m={m} onClose={() => setMoving(false)} onSaved={() => { setMoving(false); onChanged(); }} />}
      {cancelling && (
        <CancelMeetingModal m={m} onClose={() => setCancelling(false)}
          onCancel={async reason => { await crm.meetCancel(auth, m.id, reason); toast.success('Meeting cancelled. The lead is back on your Call now.'); setCancelling(false); onChanged(); }} />
      )}
    </div>
  );
};

export const MoveMeetingModal: React.FC<{ auth: CrmAuth; m: Meeting; onClose: () => void; onSaved: () => void }> = ({ auth, m, onClose, onSaved }) => {
  const [pick, setPick] = useState<MeetingPick | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!pick) { toast.error('Pick the new time'); return; }
    setSaving(true);
    try {
      const r = await crm.meetMove(auth, m.id, pick, note.trim());
      toast.success(`Moved to ${r.meeting.when}${r.meeting.open ? ': open for the coordinators free then' : ` with ${r.meeting.coordinatorName}`}${r.meeting.status === 'Link Ready' ? '. Send the new time to the student.' : ''}`);
      onSaved();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Change the meeting: ${m.name}`} sub={`Now ${m.when}, ${withWhom(m)}`} onClose={onClose} wide>
      <div className="space-y-4">
        <SlotPicker auth={auth} value={pick} onPick={setPick} meetingId={m.id} />
        <div><label className={labelCls} htmlFor="mv-note">Note (optional)</label><input id="mv-note" value={note} onChange={e => setNote(e.target.value)} className={inputCls} placeholder="Why it moved" /></div>
        <p className="text-[11px] text-gray-500">{m.open ? 'The coordinators free at the new time hear about it.' : `${m.coordinatorName} keeps it if free at the new time; if not, it is offered to whoever is free then.`} The student has to get the new time, so send it again once it shows "Link ready".</p>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600">Back</button>
          <button type="button" onClick={save} disabled={saving || !pick} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60" style={purpleBg}>{saving ? 'Saving…' : 'Move the meeting'}</button>
        </div>
      </div>
    </Modal>
  );
};

const CANCEL_REASONS = ['Student asked to cancel', 'Student not reachable', 'Booked by mistake', 'Coordinator not available'];

export const CancelMeetingModal: React.FC<{ m: Meeting; onClose: () => void; onCancel: (reason: string) => Promise<void>; reasons?: string[] }> = ({ m, onClose, onCancel, reasons = CANCEL_REASONS }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!reason.trim()) { toast.error('Say why the meeting is cancelled'); return; }
    setSaving(true);
    try { await onCancel(reason.trim()); } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Cancel the meeting: ${m.name}`} sub={`${m.when}, ${withWhom(m)}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {reasons.map(r => <button key={r} type="button" onClick={() => setReason(r)} className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border ${reason === r ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600'}`}>{r}</button>)}
        </div>
        <div><label className={labelCls} htmlFor="mc-reason">Why?</label><input id="mc-reason" value={reason} onChange={e => setReason(e.target.value)} className={inputCls} /></div>
        <p className="text-[11px] text-gray-500">The lead goes back to its salesperson, due now, to call and book again.</p>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600">Back</button>
          <button type="button" onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60">{saving ? 'Cancelling…' : 'Cancel the meeting'}</button>
        </div>
      </div>
    </Modal>
  );
};

// ── The Meetings page ────────────────────────────────────────

const Group: React.FC<{ title: string; sub?: string; list: Meeting[]; tone?: string; children: (m: Meeting) => React.ReactNode }> = ({ title, sub, list, tone = 'text-gray-800', children }) => (
  list.length ? (
    <div className="space-y-2" role="region" aria-label={title}>
      <div className="px-1">
        <h2 className={`text-sm font-bold ${tone}`}>{title} <span className="text-gray-400 font-semibold">· {list.length}</span></h2>
        {sub && <p className="text-xs text-gray-400">{sub}</p>}
      </div>
      <div className="grid gap-2 lg:grid-cols-2">{list.map(children)}</div>
    </div>
  ) : null
);

// A salesperson: their leads' meetings. Managers: everyone's (filter by
// coordinator or salesperson), and each coordinator's numbers.
export const CrmMeetings: React.FC<{ auth: CrmAuth; isManager: boolean; people?: { id: string; name: string }[]; onOpenLead?: (leadId: string) => void; tab?: string; onTab?: (t: string) => void; refresh?: number }> = ({ auth, isManager, people = [], onOpenLead, tab, onTab, refresh }) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.meetings>> | null>(null);
  const [coord, setCoord] = useState('');
  const [sp, setSp] = useState('');
  const view = isManager && tab === 'numbers' ? 'numbers' : 'list';
  const load = useCallback(async () => {
    try { setData(await crm.meetings(auth, { coordinatorId: coord, salesPersonId: sp })); } catch (e: any) { toast.error(e.message); }
  }, [auth, coord, sp]);
  useEffect(() => { load(); }, [load, refresh]);

  const card = (m: Meeting) => <MeetingCard key={m.id} auth={auth} m={m} onChanged={load} onOpenLead={onOpenLead} showOwner={isManager} />;
  const body = !data ? <Spinner label="Loading meetings…" /> : (
    !data.toSend.length && !data.waiting.length && !data.upcoming.length && !data.after.length && !data.past.length
      ? <Empty icon="🤝" title="No meetings yet" sub='Book one from Log call: "Coordinator Meeting".' />
      : (
        <div className="space-y-5">
          <Group title="Send the link to the student" tone="text-red-700" sub="The coordinator added the link. Confirm and send it on WhatsApp." list={data.toSend}>{card}</Group>
          <Group title="Waiting for a coordinator's link" tone="text-amber-800" sub="An open one goes to the first coordinator who adds the link" list={data.waiting}>{card}</Group>
          <Group title="Coming up" sub="Sent to the student" list={data.upcoming}>{card}</Group>
          <Group title="Over: waiting for the coordinator" sub="They mark whether the student joined" list={data.after}>{card}</Group>
          <Group title="Last 30 days" list={data.past}>{card}</Group>
        </div>
      )
  );
  return (
    <div className="space-y-4">
      <Head title="Meetings" sub={isManager ? 'Every coordinator meeting, and how each coordinator is doing' : 'Your leads\' meetings with the Student Coordinators'} onRefresh={view === 'list' ? load : undefined} />
      {isManager && onTab && <Tabs label="Meetings" value={view} onChange={onTab} tabs={[{ id: 'list', label: 'Meetings' }, { id: 'numbers', label: 'Coordinators' }]} />}
      {view === 'numbers' ? (
        <MeetNumbers load={f => crm.meetDashboard(auth, f)} showPeople />
      ) : (
        <>
          {isManager && data && (
            <div className="flex gap-2 flex-wrap">
              <select value={coord} onChange={e => setCoord(e.target.value)} className={`${inputCls} max-w-[220px]`} aria-label="Coordinator">
                <option value="">Every coordinator</option>
                {data.coordinators.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={sp} onChange={e => setSp(e.target.value)} className={`${inputCls} max-w-[220px]`} aria-label="Salesperson">
                <option value="">Every salesperson</option>
                {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          {body}
        </>
      )}
    </div>
  );
};

// ── Numbers (managers' Coordinators tab, the coordinator's dashboard) ──

const RANGES: { id: string; label: string }[] = [
  { id: 'week', label: 'This week' }, { id: 'lastweek', label: 'Last week' }, { id: 'month', label: 'This month' },
  { id: 'lastmonth', label: 'Last month' }, { id: '30d', label: '30 days' }, { id: 'today', label: 'Today' },
];
const rupees = (n: number) => `₹${nf(n)}`;

export const MeetNumbers: React.FC<{ load: (f: MonitorRange) => Promise<MeetDashboardData>; showPeople?: boolean }> = ({ load, showPeople }) => {
  const [range, setRange] = useState('week');
  const [data, setData] = useState<MeetDashboardData | null>(null);
  useEffect(() => {
    let live = true;
    setData(null);
    load({ range }).then(r => { if (live) setData(r); }).catch(e => toast.error(e.message));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);
  return (
    <div className="space-y-4">
      <div className="flex gap-1.5 flex-wrap" role="radiogroup" aria-label="Range">
        {RANGES.map(r => (
          <button key={r.id} type="button" role="radio" aria-checked={range === r.id} onClick={() => setRange(r.id)}
            className="px-3 py-1.5 rounded-full text-xs font-bold border" style={range === r.id ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' } : { background: '#fff', color: '#4b5563', borderColor: '#e5e7eb' }}>{r.label}</button>
        ))}
      </div>
      {!data ? <Spinner /> : (
        <>
          <p className="text-xs text-gray-500 px-1">{data.range.label}: the meetings in it (by their time), and what came of them so far</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Tile label="Meetings booked" value={nf(data.totals.booked)} sub={`${data.totals.upcoming} still to come · ${data.totals.cancelled} cancelled`} />
            <Tile label="Joined" value={nf(data.totals.joined)} sub={`${data.totals.notJoined} didn't join · ${data.totals.joinPct}% came`} />
            <Tile label="Converted" value={nf(data.totals.converted)} sub={`${rupees(data.totals.collected)} paid so far (their forms)`} />
            <Tile label="Conversion" value={`${data.totals.conversionPct}%`} sub={`${data.totals.convertedOfJoined} of the ${data.totals.joined} who joined have paid`} />
            <Tile label="Brochures sent" value={nf(data.totals.brochures || 0)} sub="after the meeting" />
            <Tile label="Polls sent" value={nf(data.totals.pollsSent)} sub={data.totals.pollsLater ? `${data.totals.pollsLater} set for later` : undefined} />
            <Tile label="Interested" value={nf(data.totals.interested)} />
            <Tile label="Not interested" value={nf(data.totals.notInterested)} />
            <Tile label="No response" value={nf(data.totals.noResponse)} />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Right now" sub="The leads coordinators handle since a meeting">
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[
                  ['Polls to update', data.now.polls, data.now.pollsDue ? `${data.now.pollsDue} due` : ''],
                  ['Waiting to pay', data.now.interested, data.now.paymentsDue ? `${data.now.paymentsDue} due` : ''],
                  ['No response', data.now.noResponse, ''],
                  ['Meetings to mark', data.totals.toMark, ''],
                ].map(([k, n, due]) => (
                  <div key={k as string} className="rounded-xl bg-gray-50 px-3 py-2">
                    <p className="text-[11px] font-semibold text-gray-500">{k}</p>
                    <p className="text-lg font-bold" style={{ color: C.ink }}>{n}{due ? <span className="text-xs font-bold text-red-600 ml-1.5">{due}</span> : null}</p>
                  </div>
                ))}
              </div>
            </Card>
            <Card title="Eight weeks" sub="Meetings booked each week (by their time)">
              <Columns ariaLabel="Meetings each week" tableHead={['Week of', 'Meetings']}
                data={data.weeks.map(w => ({ key: w.week, label: w.label.replace(/^\w+ /, ''), value: w.booked, tip: `${w.label}: ${w.booked} booked, ${w.joined} joined, ${w.converted} converted` }))} />
            </Card>
          </div>
          {showPeople && data.people.length > 0 && (
            <Card title="Each coordinator">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                    <th className="py-2 pr-3">Coordinator</th><th className="py-2 px-2 text-right">Booked</th><th className="py-2 px-2 text-right">Joined</th><th className="py-2 px-2 text-right">Didn't join</th>
                    <th className="py-2 px-2 text-right">Converted</th><th className="py-2 px-2 text-right">Conversion</th><th className="py-2 px-2 text-right">Handling now</th><th className="py-2 pl-2 text-right">To mark</th>
                  </tr></thead>
                  <tbody>
                    {data.people.map(p => (
                      <tr key={p.id} className="border-t border-gray-100">
                        <td className="py-2 pr-3 font-semibold text-gray-800">{p.name}{!p.active && <span className="text-[10px] text-gray-400"> (inactive)</span>}</td>
                        <td className="py-2 px-2 text-right">{p.booked}</td><td className="py-2 px-2 text-right">{p.joined}</td><td className="py-2 px-2 text-right">{p.notJoined}</td>
                        <td className="py-2 px-2 text-right font-bold">{p.converted}</td><td className="py-2 px-2 text-right">{p.conversionPct}%</td>
                        <td className="py-2 px-2 text-right">{p.handling}</td><td className={`py-2 pl-2 text-right ${p.toMark ? 'text-red-600 font-bold' : ''}`}>{p.toMark}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          {data.salespeople.length > 0 && (
            <Card title="Who booked them" sub="The salespeople whose leads had these meetings">
              <div className="space-y-1.5">
                {data.salespeople.map(s => (
                  <div key={s.id || s.name} className="flex items-center justify-between text-sm border-b border-gray-50 pb-1.5">
                    <span className="font-semibold text-gray-700">{s.name}</span>
                    <span className="text-gray-500 text-xs">{s.booked} booked · {s.joined} joined · <b className="text-gray-800">{s.converted} converted</b></span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
